import { useEffect, useState } from 'react';
import { EquipmentModel, InspectionModel } from '../../data/types';
import { TaskStatus } from '../../data/enums';
import { useInspection } from '../useInspection';
import InspectionUndercarriageSideScreenService from './inspectionUndercarriageSideScreen.service';

interface SideIndicators {
  name: string;
  status: TaskStatus;
  externalId: string;
}

const useInspectionUndercarriageIndicator = (inspectionId: string) => {
  const { inspection } = useInspection(inspectionId);

  const [undercarriageSideIndicator, setUndercarriageSideIndicator] =
    useState<TaskStatus>(TaskStatus.notStarted);
  const [sideIndicators, setSideIndicators] = useState<SideIndicators[]>([]);

  useEffect(() => {
    if (!inspection) {
      setUndercarriageSideIndicator(TaskStatus.notStarted);
      return;
    }

    // Get Sides from inspection
    const sides =
      inspection.asset?.equipments.filter(
        (equipment) => equipment.equipmentType === 'Crawler',
      ) || [];

    if (sides.length === 0) {
      setUndercarriageSideIndicator(TaskStatus.notStarted);
      return;
    }

    const sideIndicators = sides.map((side: EquipmentModel) => {
      const service = new InspectionUndercarriageSideScreenService(
        inspection,
        side,
      );
      const sideIndicator = service.getSideIndicators();

      const statuss = Object.values(sideIndicator).filter(
        (status) => status !== undefined,
      );

      const baseNotStarted = statuss.every(
        (status) => status === TaskStatus.notStarted,
      );

      if (baseNotStarted) {
        return {
          name: side.name,
          status: TaskStatus.notStarted,
          externalId: side.externalId,
        } as SideIndicators;
      }

      const baseLevelDone = statuss.every(
        (status) => status === TaskStatus.completed,
      );

      if (baseLevelDone) {
        return {
          name: side.name,
          status: TaskStatus.completed,
          externalId: side.externalId,
        } as SideIndicators;
      }

      return {
        name: side.name,
        status: TaskStatus.inProgress,
        externalId: side.externalId,
      };
    });

    setSideIndicators(sideIndicators);
  }, [inspection]);

  return {
    undercarriageSideIndicator,
    sideIndicators,
  };
};

export default useInspectionUndercarriageIndicator;
